import "../Reception/ReceptionCompact.css";
import { useEffect, useState } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, Spinner, Card, CardBody } from "reactstrap";
import { FaPlus, FaTrash, FaSearch, FaImage } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { productActions } from "../../sagas/productSlice";
import { formatPrice } from "../../helpers/utility";
import productService from "../../services/productService";
import Swal from "sweetalert2";
import config from "../../helpers/config";
import "./CommandeLine.css";
import POSKeypadModal from "./POSKeypadModal";
import POSNumericKeypadModal from "./POSNumericKeypadModal";
import POSProductFormModal from "./POSProductFormModal";

const ReceptionLines = ({ formik, readOnly }) => {
    const { values, setFieldValue } = formik;
    const dispatch = useDispatch();

    const [items, setItems] = useState(values.lignes || []);
    const [chooseProductModal, setChooseProductModal] = useState(false);
    const [modalSearchTerm, setModalSearchTerm] = useState("");
    const [keypadOpen, setKeypadOpen] = useState(false);
    const [keypadValue, setKeypadValue] = useState("");
    const [editIndex, setEditIndex] = useState(null);
    const [searchKeypadOpen, setSearchKeypadOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [qtyKeypadOpen, setQtyKeypadOpen] = useState(false);
    const [qtyValue, setQtyValue] = useState("");
    const [qtyIndex, setQtyIndex] = useState(null);

    const [productModal, setProductModal] = useState(false);
    const unites = useSelector((state) => state.products?.unites || []);
    const { list: categories } = useSelector((state) => state.categories || {});

    const openPriceKeypad = (index, currentValue) => {
        setEditIndex(index);
        setKeypadValue(currentValue?.toString() || "");
        setKeypadOpen(true);
    };

    const openQtyKeypad = (index, currentQty) => {
        setQtyIndex(index);
        setQtyValue(currentQty?.toString() || "1");
        setQtyKeypadOpen(true);
    };

    const confirmQtyChange = (value) => {
        const newQty = parseFloat(value);
        if (!newQty || newQty <= 0) return;

        const newItems = [...items];
        const item = newItems[qtyIndex];

        const prixHT = item.prixUnitaireHT;
        const tva = item.tauxTVA;

        item.quantiteRecue = newQty;
        item.montantHT = Number((newQty * prixHT).toFixed(2));
        item.montantTTC = Number((item.montantHT * (1 + tva)).toFixed(2));

        setItems(newItems);
        recalcTotals(newItems);
        setQtyKeypadOpen(false);
    };


    const confirmPriceChange = (value) => {
        const newItems = [...items];
        const item = newItems[editIndex];

        const tva = item.tauxTVA;
        const qty = item.quantiteRecue;

        const prixTTC = parseFloat(value) || 0;
        const prixHT = prixTTC / (1 + tva);

        item.prixUnitaireTTC = Number(prixTTC.toFixed(2));
        item.prixUnitaireHT = Number(prixHT.toFixed(2));
        item.montantHT = Number((prixHT * qty).toFixed(2));
        item.montantTTC = Number((prixTTC * qty).toFixed(2));

        setItems(newItems);
        recalcTotals(newItems);
        setKeypadOpen(false);
    };

    const { products, loading, pageSize } = useSelector(s => s.products);

    useEffect(() => {
        setItems(values.lignes || []);
    }, [values.lignes]);

    useEffect(() => {
        dispatch(productActions.findAll({
            search: modalSearchTerm,
            page: 0,
            size: 200
        }));
    }, []);

    /* ---------------- CALCULATIONS ---------------- */
    const recalcTotals = (newItems) => {
        let ht = 0, ttc = 0, taxe = 0;

        newItems.forEach(item => {
            const itemTVA = item.montantTTC - item.montantHT;
            ht += item.montantHT;
            ttc += item.montantTTC;
            taxe += itemTVA;
        });

        setFieldValue("lignes", newItems);
        setFieldValue("montantTotalHT", Number(ht.toFixed(2)));
        setFieldValue("montantTotalTaxe", Number(taxe.toFixed(2)));
        setFieldValue("montantTotalTTC", Number(ttc.toFixed(2)));
    };

    const updateQty = (index, newQty) => {
        if (newQty <= 0) return;

        const newItems = [...items];
        const item = newItems[index];

        const prixHT = item.prixUnitaireHT;
        const tva = item.tauxTVA / 100;

        item.quantiteRecue = newQty;
        item.montantHT = Number((newQty * prixHT).toFixed(2));
        item.montantTTC = Number((item.montantHT * (1 + tva)).toFixed(2));

        setItems(newItems);
        recalcTotals(newItems);
    };

    const handleDeleteItem = (index) => {
        const newItems = items.filter((_, i) => i !== index);
        setItems(newItems);
        recalcTotals(newItems);
    };

    /* ---------------- ADD PRODUCT ---------------- */
    const handleProductSelect = async (product) => {
        if (!values.fournisseurId) {
            Swal.fire("Attention", "Choisissez d'abord un fournisseur", "warning");
            return;
        }

        const exists = items.some(i => i.articleId === product.id);
        if (exists) {
            Swal.fire("Déjà ajouté", "Produit déjà dans la réception", "info");
            return;
        }

        let supplierPrice = await productService.findPrixAchat(product.id, values.fournisseurId);
        supplierPrice = parseFloat(supplierPrice) || 0;

        const tva = product.tauxTVAAchat / 100;
        const prixHT = supplierPrice;
        const prixTTC = prixHT * (1 + tva);

        const newItem = {
            articleId: product.id,
            articleNom: product.libelle,
            unite: product.unite,
            quantiteRecue: 1,
            prixUnitaireHT: Number(prixHT.toFixed(2)),
            prixUnitaireTTC: Number(prixTTC.toFixed(2)),
            tauxTVA: product.tauxTVAAchat,
            montantHT: Number(prixHT.toFixed(2)),
            montantTTC: Number(prixTTC.toFixed(2))
        };

        const newItems = [...items, newItem];
        setItems(newItems);
        recalcTotals(newItems);
        setChooseProductModal(false);
    };

    const handleSearchConfirm = () => {
        setSearchKeypadOpen(false);

        dispatch(productActions.findAll({
            search: searchValue,
            page: 0,
            size: 200
        }));
    };

    return (
        <>
            {/* 🟢 POS CART */}
            <div className="pos-cart reception-compact reception-lines">
                {items.length === 0 ? (
                    <div className="pos-empty">Aucun article ajouté</div>
                ) : (
                    items.map((item, index) => (
                        <div key={index} className="pos-cart-row">
                            <div className="pos-cart-info">
                                <div className="pos-cart-name">{item.articleNom}</div>
                                <div className="pos-cart-sub">{item.unite}</div>
                            </div>

                            <div className="pos-cart-controls">
                                <button className="pos-qty-btn" onClick={() => !readOnly && updateQty(index, item.quantiteRecue - 1)} type="button">−</button>
                                <div
                                    className="pos-qty-value editable"
                                    onClick={() => !readOnly && openQtyKeypad(index, item.quantiteRecue)}
                                >
                                    {item.quantiteRecue}
                                </div>
                                <button className="pos-qty-btn" onClick={() => !readOnly && updateQty(index, item.quantiteRecue + 1)} type="button">+</button>
                            </div>

                            <div
                                className="pos-cart-price editable"
                                onClick={() => !readOnly && openPriceKeypad(index, item.prixUnitaireTTC)}
                            >
                                {formatPrice(item.prixUnitaireTTC)}
                                <div className="pos-price-sub">TTC</div>
                            </div>

                            <div className="pos-cart-price">{formatPrice(item.montantTTC)}</div>

                            <button type="button" aria-label={`Retirer ${item.articleNom}`} disabled={readOnly} className="pos-delete-btn" onClick={() => !readOnly && handleDeleteItem(index)}>
                                <FaTrash />
                            </button>
                        </div>
                    ))
                )}
            </div>

            {/* 🟢 ADD PRODUCT BAR */}
            <Button type="button" color="primary" outline className="pos-add-bar reception-add-article" disabled={readOnly} onClick={() => setChooseProductModal(true)}>
                <FaPlus /> Ajouter un article
            </Button>

            {/* 🟢 PRODUCT SELECTOR MODAL */}
            <Modal isOpen={chooseProductModal} toggle={() => setChooseProductModal(false)} size="xl"  className="reception-compact-modal">
                <ModalHeader
                    toggle={() => setChooseProductModal(false)}
                    className="d-flex justify-content-between align-items-center"
                >
                    <span>Choisir Produit</span>

                    {!readOnly && (
                        <Button
                            color="success"
                            size="sm"
                            className="fw-bold px-3"
                            onClick={() => setProductModal(true)}
                        >
                            <FaPlus className="me-1" /> Nouveau produit
                        </Button>
                    )}
                </ModalHeader>

                <ModalBody>
                    <div
                        className="pos-product-search"
                        onClick={() => setSearchKeypadOpen(true)}
                    >
                        <FaSearch />
                        <div className="pos-search-display">
                            {searchValue || "Rechercher produit..."}
                        </div>
                    </div>

                    <Row className="g-3">
                        {loading ? <Spinner /> : products.map(product => (
                            <Col md={3} key={product.id}>
                                <Card className="product-card" onClick={() => handleProductSelect(product)}>
                                    {product.photo ? (
                                        <img src={`${config.baseURL}${product.photo}`} alt="" className="card-img-top" />
                                    ) : (
                                        <div className="bg-placeholder"><FaImage size={30} /></div>
                                    )}
                                    <CardBody>
                                        <div className="fw-bold">{product.libelle}</div>
                                        <div>{formatPrice(product.prixVenteTTC1)}</div>
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button onClick={() => setChooseProductModal(false)}>Fermer</Button>
                </ModalFooter>
            </Modal>

            <POSNumericKeypadModal
                isOpen={keypadOpen}
                toggle={() => setKeypadOpen(false)}
                value={keypadValue}
                onChange={setKeypadValue}
                onConfirm={confirmPriceChange}
                title="Modifier Prix TTC"
            />

            <POSNumericKeypadModal
                isOpen={qtyKeypadOpen}
                toggle={() => setQtyKeypadOpen(false)}
                value={qtyValue}
                onChange={setQtyValue}
                onConfirm={confirmQtyChange}
                title="Saisir Quantité"
            />

            <POSKeypadModal
                isOpen={searchKeypadOpen}
                toggle={() => setSearchKeypadOpen(false)}
                value={searchValue}
                onChange={setSearchValue}
                onSearch={handleSearchConfirm}
                title="Recherche Produit"
            />

            <Modal
                isOpen={productModal}
                toggle={() => setProductModal(false)}
                size="xl"
                centered
                className="pos-product-modal"
            >
                <ModalHeader toggle={() => { setProductModal(false); }}>
                    {"Nouveau produit"}
                </ModalHeader>

                <ModalBody style={{ maxHeight: "80vh", overflowY: "auto" }}>
                    <POSProductFormModal
                        onClose={() => {
                            setProductModal(false);
                            handleSearchConfirm(); // refresh list after save
                        }}
                        categories={categories}
                        unites={unites}
                    />
                </ModalBody>
            </Modal>
        </>
    );
};

export default ReceptionLines;
